<template>
  <div class="schema-tree">
    <div
      v-if="showSearchBox"
      class="filter-section"
    >
      <Input
        v-model="searchText"
        size="small"
        placeholder="Filter by Keyword"
        clearable
        class="search-box"
      />
      <Checkbox
        v-model="includeColumnsInSearch"
        class="checkbox"
        @on-change="updateSearchScope"
      >
        Include <strong>Columns</strong> in search
      </Checkbox>
    </div>
    <TreeNode
      v-for="(item, index) in treeData"
      :key="index"
      :schema="item"
      :schema-path="`${index}`"
      :arrows-type="arrowsType"
      :expand="expand"
      @schemaPath="createPath"
      @showColumnDetails="showColumnDetails"
    />
    <div v-if="!treeData.length">
      {{ localeEmptyText }}
    </div>
  </div>
</template>

<script>
import TreeNode from './SchemaTreeNode.vue'
import _ from 'lodash'

export default {

  components: {
    TreeNode
  },
  props: {
    value: {
      type: Array,
      default: () => []
    },
    arrowsType: {
      type: String,
      default: 'angle'
    },
    emptyText: String,
    showSearchBox: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      treeData: this.value,
      expand: true,
      filterText: '',
      includeColumnsInSearch: false
    }
  },

  computed: {
    localeEmptyText () {
      if (typeof this.emptyText === 'undefined') {
        return 'No Data'
      } else {
        return this.emptyText
      }
    },

    searchText: {
      get () {
        return this.filterText
      },
      set: _.debounce(function (searchText) {
        this.filterText = searchText
        this.treeData = this.filteredTree(this.value)
      }, 500)
    }
  },

  watch: {
    value (newValue) {
      this.treeData = this.showSearchBox ? this.filteredTree(newValue) : newValue
    }
  },

  methods: {
    /**
       * Returns the schema path to the parent.
       * @param {String} path
       */
    createPath (path) {
      this.$emit('schemaPath', path)
    },

    /**
       * Toggles the search scope including/excluding the columns.
       */
    updateSearchScope () {
      this.treeData = this.filteredTree(this.value)
    },

    /**
       * Filters the schema tree according the provided keyword.
       * NOTE: It will return only those schemas (with its parent path) that matches with the given keyword.
       * @param {Array} schema
       * @return {Array}
       */
    filteredTree (schema) {
      if (!this.filterText) {
        return schema
      }
      const selectedNodes = []
      schema.forEach(schemaItem => {
        const currentSchema = {
          title: schemaItem.title,
          icon: schemaItem.icon,
          excludeFromPath: schemaItem.excludeFromPath || false
        }

        const schemaFullContent = schemaItem.hasOwnProperty('schemaContent') ? schemaItem.schemaContent : (schemaItem.hasOwnProperty('schemaView') ? schemaItem.schemaView : [])
        if (schemaFullContent.length) {
          const filteredSchemaContent =
              (schemaItem.type === 'table' || schemaItem.type === 'view') && !this.includeColumnsInSearch
                ? [] : this.filteredTree(schemaFullContent)

          if (filteredSchemaContent.length) {
            this.$set(currentSchema, 'schemaView', [...filteredSchemaContent])
          }
        }

        const isSchemaViewInFilter = currentSchema.title.toLowerCase().includes(this.filterText.toLowerCase())
        const isSchemaContentFiltered = currentSchema.hasOwnProperty('schemaView')
        if (isSchemaViewInFilter || isSchemaContentFiltered) {
          this.$set(currentSchema, 'expand', isSchemaContentFiltered)

          if (isSchemaViewInFilter && !isSchemaContentFiltered && schemaFullContent.length) {
            this.$set(currentSchema, 'schemaView', [])
            this.$set(currentSchema, 'schemaContent', schemaFullContent)
            this.$set(currentSchema, 'expand', false)
          }
          selectedNodes.push(currentSchema)
        }
      })

      return selectedNodes
    },

    showColumnDetails (status, schemaPosition, schemaData) {
      this.$emit('showColumnDetails', status, schemaPosition, schemaData)
    }
  }
}
</script>

<style>
  .search-box input,
  .checkbox {
    margin-bottom: 8px;
  }

  .schema-tree .filter-section {
    position: sticky;
    top: 0px;
    z-index: 100;
    background-color: var(--color-white);
  }

  .schema-tree ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .schema-tree li ul {
    padding-left: 18px;
  }
</style>
